import React, { useState, useEffect } from "react";
import { useLocation, Link } from 'react-router-dom';
import { Button, Modal, Form, OverlayTrigger, Tooltip } from "react-bootstrap";
import { error, success } from "../../../notify";
import axios from 'axios';

const ExcludedDomains = () => {
    const [domains, setDomains] = useState([]);
    const [filteredDomains, setFilteredDomains] = useState([]);
    const [currentDomain, setCurrentDomain] = useState("");
    const [domainToEditIndex, setDomainToEditIndex] = useState(null);
    const [, setHasRecord] = useState(false);
    const [searchInputValue, setSearchInputValue] = useState("");
    const [showClearButton, setShowClearButton] = useState(false);

    const [showAddEditModal, setShowAddEditModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);

    const location = useLocation();
    const { siteId, siteName } = location.state || {};

    // API endpoints
    const baseApiUrl = `${process.env.REACT_APP_SERVER_URL}/api/website/excluded_domains`;

    // Search handlers
    const handleSearch = () => {
        if (searchInputValue.trim() !== "") {
            setShowClearButton(true);
            const filtered = domains.filter(item =>
                item.excluded_domains.toLowerCase().includes(searchInputValue.toLowerCase())
            );
            setFilteredDomains(filtered);
        }
    };

    const handleClearSearch = () => {
        setSearchInputValue("");
        setShowClearButton(false);
        setFilteredDomains(domains);
    };

    const handleSearchInputChange = (event) => {
        setSearchInputValue(event.target.value);
        if (event.target.value.trim() === "") {
            setShowClearButton(false);
            setFilteredDomains(domains);
        }
    };

    const handleSearchKeyPress = (event) => {
        if (event.key === 'Enter') {
            handleSearch();
        }
    };

    // Refresh
    const handleRefresh = async () => {
        try {
            await fetchDomains();
            success("Excluded domains refreshed successfully.", true);
        } catch (err) {
            error(err.response?.data?.message || "Failed to refresh excluded domains.", true);
        }
    };

    // Fetch domains from server — stores full record objects { id, website_id, excluded_domains }
    const fetchDomains = async () => {
        try {
            const response = await axios.get(`${baseApiUrl}/${siteId}`);
            const records = response.data;
            if (!records || records.length === 0) {
                setDomains([]); setFilteredDomains([]); setHasRecord(false);
                return;
            }
            setDomains(records);
            setFilteredDomains(records);
            setHasRecord(true);
        } catch (err) {
            if (err.response?.status === 404) {
                setDomains([]); setFilteredDomains([]); setHasRecord(false);
            } else {
                error(err.response?.data?.message || "Failed to fetch excluded domains.", true);
            }
        }
    };

    useEffect(() => {
        if (siteId) {
            fetchDomains();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [siteId]);

    // Save — POST for new, PUT for edit (identified by recordId)
    const saveDomains = async (domain, actionDescription = "added", recordId = null) => {
    try {
        const payload = { excluded_domains: domain.trim() };
        if (recordId) {
            // PUT /{record_id} — no website_id in URL
            await axios.put(`${baseApiUrl}/${recordId}`, payload);
        } else {
            await axios.post(`${baseApiUrl}/${siteId}`, payload);
        }
        await fetchDomains();
        success(`Excluded domain ${actionDescription} successfully.`, true);
    } catch (err) {
        error(err.response?.data?.message || `Failed to ${actionDescription} excluded domain.`, true);
        throw err;
    }
};

    // Modal handlers
    const handleCloseAddEditModal = () => {
        setCurrentDomain("");
        setDomainToEditIndex(null);
        setShowAddEditModal(false);
    };

    const handleCloseDeleteModal = () => setShowDeleteModal(false);

    const handleShowAddModal = () => {
        setCurrentDomain("");
        setDomainToEditIndex(null);
        setShowAddEditModal(true);
    };

    const handleShowEditModal = (index) => {
        setCurrentDomain(domains[index].excluded_domains);
        setDomainToEditIndex(index);
        setShowAddEditModal(true);
    };

    const handleShowDeleteModal = (index) => {
        setDomainToEditIndex(index);
        setShowDeleteModal(true);
    };

    // Add / Edit submission
    const handleAddEditDomain = async () => {
        if (currentDomain.trim() === "") {
            error("Domain cannot be empty.", true);
            return;
        }
        try {
            const recordId = domainToEditIndex !== null ? domains[domainToEditIndex].id : null;
            await saveDomains(
                currentDomain.trim(),
                domainToEditIndex !== null ? "updated" : "added",
                recordId
            );
            handleCloseAddEditModal();
        } catch {
            // Error already shown by saveDomains
        }
    };

    // Delete confirmation
    const handleDeleteDomain = async () => {
    if (domainToEditIndex !== null) {
        const recordId = domains[domainToEditIndex].id;
        try {
            await axios.delete(`${baseApiUrl}/${recordId}`);
            await fetchDomains();
            success("Excluded domain deleted successfully.", true);
            handleCloseDeleteModal();
        } catch (err) {
            error(err.response?.data?.message || "Failed to delete excluded domain.", true);
        }
    }
};

    // Icons
    const EditIcon = () => (
        <svg width="16" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M13.7476 20.4428H21.0002" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            <path fillRule="evenodd" clipRule="evenodd" d="M12.78 3.79479C13.5557 2.86779 14.95 2.73186 15.8962 3.49173C15.9485 3.53296 17.6295 4.83879 17.6295 4.83879C18.669 5.46719 18.992 6.80311 18.3494 7.82259C18.3153 7.87718 8.81195 19.7645 8.81195 19.7645C8.49578 20.1589 8.01583 20.3918 7.50291 20.3973L3.86353 20.443L3.04353 16.9723C2.92866 16.4843 3.04353 15.9718 3.3597 15.5773L12.78 3.79479Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M11.021 6.00098L16.4732 10.1881" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    );

    const DeleteIcon = () => (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M19.3248 9.46826C19.3248 9.46826 18.7818 16.2033 18.4668 19.0403C18.3168 20.3953 17.4798 21.1893 16.1088 21.2143C13.4998 21.2613 10.8878 21.2643 8.27979 21.2093C6.96079 21.1823 6.13779 20.3783 5.99079 19.0473C5.67379 16.1853 5.13379 9.46826 5.13379 9.46826" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M20.708 6.23975H3.75" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M17.4406 6.23973C16.6556 6.23973 15.9796 5.68473 15.8256 4.91573L15.5826 3.69973C15.4326 3.13873 14.9246 2.75073 14.3456 2.75073H10.1126C9.53358 2.75073 9.02558 3.13873 8.87558 3.69973L8.63258 4.91573C8.47858 5.68473 7.80258 6.23973 7.01758 6.23973" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    );

    const RefreshIcon = () => (
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#5a6acf" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M3 2v6h6"></path>
            <path d="M21 12a9 9 0 0 0-15-6.7L3 8"></path>
            <path d="M21 22v-6h-6"></path>
            <path d="M3 12a9 9 0 0 0 15 6.7L21 16"></path>
        </svg>
    );

    const BackIcon = () => (
        <svg xmlns="http://www.w3.org/2000/svg" width="23" height="23" viewBox="0 0 24 24" fill="none" stroke="#000000" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="15 18 9 12 15 6"></polyline>
            <line x1="5" y1="6" x2="5" y2="18"></line>
        </svg>
    );

    const SearchIcon = () => (
        <svg xmlns="http://www.w3.org/2000/svg" width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="11" cy="11" r="8"></circle>
            <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
        </svg>
    );

    const ClearIcon = () => (
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
        </svg>
    );

    return (
        <div style={{
            fontFamily: '"Inter", sans-serif',
            background: '#f8f9fd',
            margin: 0,
            padding: '20px',
            minHeight: '100vh',
            width: '100%',
            boxSizing: 'border-box'
        }}>
            <div style={{
                width: '100%',
                maxWidth: '100%',
                overflowX: 'auto',
                boxSizing: 'border-box'
            }}>
                <div style={{
                    background: '#fff',
                    borderRadius: '16px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                    padding: '20px',
                    width: '100%',
                    maxWidth: '100%',
                    boxSizing: 'border-box'
                }}>
                    {/* Header with search and buttons */}
                    <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: '15px',
                        flexWrap: 'wrap',
                        gap: '12px'
                    }}>
                        <h2 style={{ fontSize: '20px', margin: 0, fontWeight: 600 }}>
                            Excluded Domains - {siteName}
                        </h2>

                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                            {/* Search box */}
                            <div style={{ position: 'relative', width: '350px', minWidth: '250px' }}>
                                <input
                                    type="text"
                                    placeholder="Search domains..."
                                    value={searchInputValue}
                                    onChange={handleSearchInputChange}
                                    onKeyPress={handleSearchKeyPress}
                                    style={{
                                        width: '100%',
                                        padding: '8px 68px 8px 12px',
                                        border: '1px solid #ddd',
                                        borderRadius: '8px',
                                        fontSize: '14px'
                                    }}
                                />
                                <div style={{
                                    position: 'absolute',
                                    right: '4px',
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    display: 'flex',
                                    gap: '4px',
                                    alignItems: 'center'
                                }}>
                                    {showClearButton && (
                                        <Button
                                            variant="outline-secondary"
                                            onClick={handleClearSearch}
                                            style={{
                                                border: '1px solid #ddd',
                                                borderRadius: '8px',
                                                width: '28px',
                                                height: '28px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                padding: '0',
                                                cursor: 'pointer',
                                                color: '#5a6acf',
                                                transition: 'all 0.2s ease-in-out',
                                                backgroundColor: 'white',
                                                boxShadow: '0 1px 1px rgba(0, 0, 0, 0.15)',
                                            }}
                                            onMouseEnter={(e) => {
                                                e.currentTarget.style.backgroundColor = '#5a6acf';
                                                e.currentTarget.style.color = 'white';
                                            }}
                                            onMouseLeave={(e) => {
                                                e.currentTarget.style.backgroundColor = 'white';
                                                e.currentTarget.style.color = '#5a6acf';
                                            }}
                                        >
                                            <ClearIcon />
                                        </Button>
                                    )}
                                    <Button
                                        variant="primary"
                                        onClick={handleSearch}
                                        style={{
                                            border: '1px solid #ddd',
                                            borderRadius: '8px',
                                            width: '28px',
                                            height: '28px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            padding: '0',
                                            cursor: 'pointer',
                                            color: '#5a6acf',
                                            transition: 'all 0.2s ease-in-out',
                                            backgroundColor: 'white',
                                            boxShadow: '0 1px 1px rgba(0, 0, 0, 0.15)',
                                        }}
                                        onMouseEnter={(e) => {
                                            e.currentTarget.style.backgroundColor = '#5a6acf';
                                            e.currentTarget.style.color = 'white';
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.style.backgroundColor = 'white';
                                            e.currentTarget.style.color = '#5a6acf';
                                        }}
                                    >
                                        <SearchIcon />
                                    </Button>
                                </div>
                            </div>

                            <Button
                                variant="outline-secondary"
                                onClick={handleShowAddModal}
                                style={{
                                    background: 'white',
                                    border: '1px solid #ddd',
                                    borderRadius: '8px',
                                    padding: '8px 14px',
                                    fontSize: '14px',
                                    color: '#5a6acf',
                                    cursor: 'pointer',
                                    flexShrink: 0
                                }}
                            >
                                + Add Domain
                            </Button>
                        </div>

                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <Button
                                variant="outline-secondary"
                                onClick={handleRefresh}
                                style={{
                                    background: 'white',
                                    border: '1px solid #ddd',
                                    borderRadius: '50%',
                                    width: '40px',
                                    height: '40px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    padding: '0',
                                    fontSize: '14px',
                                    color: '#5a6acf',
                                    cursor: 'pointer',
                                    flexShrink: 0
                                }}
                            >
                                <RefreshIcon />
                            </Button>
                            <Button
                                variant="outline-secondary"
                                style={{
                                    background: 'white',
                                    border: '1px solid #ddd',
                                    borderRadius: '50%',
                                    width: '40px',
                                    height: '40px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    padding: '0',
                                    fontSize: '14px',
                                    color: '#5a6acf',
                                    cursor: 'pointer',
                                    flexShrink: 0
                                }}
                            >
                                <Link
                                    to="/site"
                                    style={{ color: 'inherit', textDecoration: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                >
                                    <BackIcon />
                                </Link>
                            </Button>
                        </div>
                    </div>

                    {/* Table */}
                    <div style={{ width: '100%', overflowX: 'auto' }}>
                        <table style={{
                            width: '100%',
                            borderCollapse: 'collapse',
                            minWidth: '500px'
                        }}>
                            <thead>
                                <tr style={{ textAlign: 'left', fontWeight: 600, fontSize: '14px', color: '#555' }}>
                                    <th style={{ padding: '14px 10px', borderBottom: '1px solid #f0f0f0', width: '70%' }}>Domain</th>
                                    <th style={{ padding: '14px 10px', borderBottom: '1px solid #f0f0f0', textAlign: 'center', width: '30%' }}>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredDomains.map((record, index) => (
                                    <tr key={record.id}>
                                        <td style={{ padding: '14px 10px', borderBottom: '1px solid #f0f0f0' }}>
                                            {record.excluded_domains}
                                        </td>
                                        <td style={{ padding: '14px 10px', borderBottom: '1px solid #f0f0f0', textAlign: 'center' }}>
                                            <div style={{ display: 'flex', justifyContent: 'center', gap: '12px', alignItems: 'center', color: '#555' }}>
                                                <OverlayTrigger placement="top" overlay={<Tooltip>Edit</Tooltip>}>
                                                    <span
                                                        onClick={() => handleShowEditModal(index)}
                                                        style={{ cursor: 'pointer', display: 'inline-flex' }}
                                                    >
                                                        <EditIcon />
                                                    </span>
                                                </OverlayTrigger>

                                                <OverlayTrigger placement="top" overlay={<Tooltip>Delete</Tooltip>}>
                                                    <span
                                                        onClick={() => handleShowDeleteModal(index)}
                                                        style={{ cursor: 'pointer', display: 'inline-flex' }}
                                                    >
                                                        <DeleteIcon />
                                                    </span>
                                                </OverlayTrigger>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {/* Empty state */}
                    {filteredDomains.length === 0 && (
                        <div style={{ textAlign: 'center', padding: '40px', color: '#888' }}>
                            {domains.length === 0 ? 'No excluded domains added yet.' : 'No domains matching your search.'}
                        </div>
                    )}
                </div>

                {/* Add/Edit Modal */}
                <Modal show={showAddEditModal} onHide={handleCloseAddEditModal}>
                    <Modal.Header closeButton>
                        <Modal.Title>{domainToEditIndex !== null ? "Edit Domain" : "Add Domain"}</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <Form>
                            <Form.Group className="mb-3">
                                <Form.Label>Domain</Form.Label>
                                <Form.Control
                                    type="text"
                                    placeholder="e.g. example.com"
                                    value={currentDomain}
                                    onChange={(e) => setCurrentDomain(e.target.value)}
                                />
                            </Form.Group>
                        </Form>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button
                            variant="secondary"
                            onClick={handleCloseAddEditModal}
                            style={{ border: '1px solid #ddd', minWidth: '100px' }}
                        >
                            Close
                        </Button>
                        <Button
                            variant="primary"
                            onClick={handleAddEditDomain}
                            style={{
                                backgroundColor: '#5a6acf',
                                borderColor: '#5a6acf',
                            }}
                        >
                            {domainToEditIndex !== null ? "Save Changes" : "Add Domain"}
                        </Button>
                    </Modal.Footer>
                </Modal>

                {/* Delete Confirmation Modal */}
                <Modal show={showDeleteModal} onHide={handleCloseDeleteModal}>
                    <Modal.Header closeButton>
                        <Modal.Title>Confirm Delete</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>Are you sure you want to delete this domain?</Modal.Body>
                    <Modal.Footer>
                        <Button
                            variant="secondary"
                            onClick={handleCloseDeleteModal}
                            style={{ border: '1px solid #ddd', minWidth: '100px' }}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="danger"
                            onClick={handleDeleteDomain}
                            style={{ minWidth: '100px' }}
                        >
                            Delete
                        </Button>
                    </Modal.Footer>
                </Modal>
            </div>
        </div>
    );
};

export default ExcludedDomains;